/*#
# The fold

`fold(events)` turns a slice of the log into the current state of every task
it mentions. It is incremental — `applyEvent` takes one event at a time — so a
client that holds a cursor replays only what it hasn't seen.

Task numbers are **dense over `created` events in log order**, so `#38` is the
38th task the log ever saw and never changes. Numbering is derived, not stored:
that is what lets a task created offline receive its number when the remote
log commits it.

```js
import { fold, MemoryLog, VirtaStore, counterIds, stepClock } from 'tosijs-virta'

const store = new VirtaStore({ log: new MemoryLog(), identity: 'demo', ids: counterIds(), clock: stepClock() })
await store.load()
const a = await store.create({ title: 'first', tags: ['bug'] })
await store.create({ title: 'second' })
await store.tag(a.id, 'status:doing')
await store.comment(a.id, 'on it')

preview.textContent = store.tasks.map((t) => `#${t.num} ${t.title} [${t.tags.join(' ')}] comments=${t.comments}`).join('\n')
```
```test
test('two tasks, numbered in creation order, with the fold applied', () => {
  expect(preview.textContent).toBe('#1 first [bug status:doing] comments=1\n#2 second [] comments=0')
})
```
*/

/*{ "parent": "Reference", "order": 30 }*/

import {
  DISPATCH_KEYS,
  PROJECT_SETTING_KEYS,
  textHash,
  type ProjectProfile,
  type Task,
  type VirtaEvent,
  type Correction,
} from './model'
import { isPublic } from './public'

export interface FoldState {
  tasks: Map<string, Task>
  /** project profiles by name; only projects whose settings were ever set */
  projects: Map<string, ProjectProfile>
  nextNum: number
  /** `seq` of the last event applied; 0 before anything. */
  cursor: number
  /** event ids applied so far: an id seen again, even at a new seq, is ignored */
  seen: Set<string>
  /** Events whose task the fold has never seen. Counted, never thrown. */
  orphans: number
  /** which practice made each automatic change (board #2474): an undo is counted against it */
  practicedBy: Map<string, string>
  /** corrections of attribution, in log order (board #2752) */
  corrections: Correction[]
}

export function newFoldState(): FoldState {
  return {
    tasks: new Map(),
    projects: new Map(),
    nextNum: 1,
    cursor: 0,
    seen: new Set(),
    orphans: 0,
    practicedBy: new Map(),
    corrections: [],
  }
}

/** A practice's running record, started at its first application. */
const noCounts = () => ({
  suggested: 0,
  accepted: 0,
  overruled: 0,
  applied: 0,
  undone: 0,
})

/**
 * Count one outcome on a practice's record: in its totals, and under the
 * measure in force right now (the fold runs in log order, so that is the
 * measure the outcome was collected under; board #2475).
 */
function count(
  practice: Task,
  outcome: keyof import('./model').PracticeCounts
): void {
  const record = (practice.record ??= { ...noCounts(), byMeasure: {} })
  record[outcome]++
  const version = String(practice.measure?.version ?? 0)
  ;(record.byMeasure[version] ??= noCounts())[outcome]++
}

/** Apply one event. Returns the task it changed, or `undefined` for an orphan. */
export function applyEvent(
  state: FoldState,
  event: VirtaEvent
): Task | undefined {
  // Idempotent by id as well as by seq: a host that re-sequenced a document
  // (tosijs-platform#25) must not make a replica apply the same event twice.
  if (state.seen.has(event.id)) return undefined
  state.seen.add(event.id)
  if (event.seq > state.cursor) state.cursor = event.seq
  if (event.kind === 'created') {
    const task: Task = {
      id: event.taskId,
      num: state.nextNum++,
      title: event.payload.title,
      body: event.payload.body,
      tags: dedupe(event.payload.tags),
      createdAt: event.at,
      createdBy: event.by,
      updatedAt: event.at,
      statusAt: event.at,
      closedAt: null,
      subscribers: [],
      comments: 0,
      attachments: 0,
      showcase: null,
      measure: null,
      procedure: null,
      record: null,
      pending: [],
      conflicts: [],
    }
    state.tasks.set(task.id, task)
    return task
  }
  // a correction of attribution (board #2752): kept for whoever displays
  // authorship; it changes no task
  if (event.kind === 'reattributed') {
    state.corrections.push({
      id: event.id,
      at: event.at,
      by: event.by,
      ...event.payload,
      ...(event.attribution ? { stamp: event.attribution } : {}),
    })
    return undefined
  }
  if (event.kind === 'project') {
    const { name, settings } = event.payload
    const profile: ProjectProfile = state.projects.get(name) ?? {
      ...emptySettings(),
      name,
      updatedAt: event.at,
      updatedBy: event.by,
    }
    for (const key of PROJECT_SETTING_KEYS) {
      const value = settings[key]
      // making a project public is a person's decision, signed in on the
      // board: a token may turn it off, never on (a local log has no stamps)
      if (
        key === 'public' &&
        event.attribution?.token &&
        isPublic({ public: String(value ?? '') })
      )
        continue
      // dispatch spends paid turns: a person's switch (board #161) — a
      // token may turn it off (clear `dispatch`), never on, never retune
      if (
        (DISPATCH_KEYS as readonly string[]).includes(key) &&
        event.attribution?.token &&
        !(key === 'dispatch' && String(value ?? '').trim() === '')
      )
        continue
      if (typeof value === 'string') profile[key] = value
    }
    profile.updatedAt = event.at
    profile.updatedBy = event.by
    state.projects.set(name, profile)
    return undefined
  }
  const task = state.tasks.get(event.taskId)
  if (!task) {
    state.orphans++
    return undefined
  }
  task.updatedAt = event.at
  switch (event.kind) {
    case 'retitled':
      task.title = event.payload.title
      break
    case 'edited': {
      const base = event.payload.base
      if (
        base !== undefined &&
        base !== textHash(task.body) &&
        event.payload.body !== task.body
      ) {
        // made from an older version: a concurrent edit — both kept,
        // flagged, never last-write-wins (board #2473)
        if (!task.conflicts.some((c) => c.id === event.id))
          task.conflicts.push({
            id: event.id,
            body: event.payload.body,
            by: event.by,
            at: event.at,
          })
      } else {
        task.body = event.payload.body
        // an edit made from the current body settles what was flagged
        if (base !== undefined) task.conflicts = []
      }
      break
    }
    case 'tagged':
      if (!task.tags.includes(event.payload.tag)) {
        task.tags.push(event.payload.tag)
        if (event.payload.tag.startsWith('status:')) task.statusAt = event.at
      }
      break
    case 'untagged':
      if (task.tags.includes(event.payload.tag)) {
        task.tags = task.tags.filter((t) => t !== event.payload.tag)
        if (event.payload.tag.startsWith('status:')) task.statusAt = event.at
      }
      break
    case 'commented':
      task.comments++
      break
    case 'attached':
      task.attachments++
      break
    case 'procedure':
      task.procedure = {
        when: event.payload.when,
        set: [...event.payload.set],
        version: (task.procedure?.version ?? 0) + 1,
        at: event.at,
      }
      break
    case 'suggested': {
      if (!task.pending.some((p) => p.id === event.id))
        task.pending.push({
          id: event.id,
          practice: event.payload.practice,
          set: [...event.payload.set],
          text: event.payload.text,
        })
      const practice = state.tasks.get(event.payload.practice)
      if (practice) count(practice, 'suggested')
      break
    }
    case 'practiced': {
      state.practicedBy.set(event.id, event.payload.practice)
      const practice = state.tasks.get(event.payload.practice)
      if (practice) count(practice, 'applied')
      break
    }
    case 'resolved': {
      const suggestion = task.pending.find((p) => p.id === event.payload.of)
      task.pending = task.pending.filter((p) => p.id !== event.payload.of)
      const of = suggestion?.practice ?? state.practicedBy.get(event.payload.of)
      const practice = of ? state.tasks.get(of) : undefined
      if (practice) count(practice, event.payload.outcome)
      break
    }
    case 'measured':
      task.measure = {
        measure: event.payload.measure,
        death: event.payload.death,
        version: (task.measure?.version ?? 0) + 1,
        at: event.at,
        by: event.by,
      }
      break
    case 'showcased':
      task.showcase = event.payload.withdrawn
        ? null
        : {
            title: event.payload.title,
            text: event.payload.text,
            media: event.payload.media,
            alt:
              event.payload.alt || `screen capture of ${event.payload.title}`,
            link: event.payload.link,
            ...(event.payload.file ? { file: event.payload.file } : {}),
            at: event.at,
            by: event.by,
          }
      break
    case 'closed':
      task.closedAt = event.at
      break
    case 'reopened':
      task.closedAt = null
      break
    case 'subscribed':
      if (!task.subscribers.includes(event.payload.subscriber)) {
        task.subscribers.push(event.payload.subscriber)
      }
      break
    case 'unsubscribed':
      task.subscribers = task.subscribers.filter(
        (s) => s !== event.payload.subscriber
      )
      break
  }
  return task
}

/** Fold a whole slice from scratch. */
export function fold(events: Iterable<VirtaEvent>): FoldState {
  const state = newFoldState()
  for (const event of events) applyEvent(state, event)
  return state
}

function dedupe(tags: readonly string[]): string[] {
  return Array.from(new Set(tags))
}

export function emptySettings(): import('./model').ProjectSettings {
  return {
    public: '',
    icon: '',
    color: '',
    background: '',
    description: '',
    site: '',
    package: '',
    repo: '',
    version: '',
    retired: '',
    enrolled: '',
    practices: '',
    dispatch: '',
    dispatchEvery: '',
    dispatchCap: '',
  }
}
